import type { RoofType, StylePreset } from '@/model/schema';
import { DEFAULT_PALETTES } from '@/render/materials';
import type { PaletteConfig } from '@/model/schema';

/**
 * Style presets are clustered parameter grammars. Sampling each variable
 * globally produces random-feeling houses; sampling from a preset's clustered
 * distribution produces houses that feel "composed".
 *
 * See design doc §"Style Presets". Each entry constrains:
 *  - lot footprint preferences
 *  - story count distribution
 *  - preferred roof types (weighted)
 *  - window density
 *  - accessory preferences (fence/mailbox)
 *  - palette
 */
export interface StylePresetSpec {
  id: StylePreset;
  label: string;
  description: string;
  lotWidth: { min: number; max: number };
  lotDepth: { min: number; max: number };
  footprintCoverage: { min: number; max: number }; // fraction of buildable area
  aspectRatio: { min: number; max: number }; // width / depth of footprint
  storyHeight: { min: number; max: number };
  storyCountWeights: Record<1 | 2 | 3, number>;
  roofWeights: Record<RoofType, number>;
  roofOverhang: { min: number; max: number };
  windowDensity: number; // openings per meter of facade
  minCornerOffset: number; // meters from corner to nearest opening
  fenceChance: number;
  mailboxChance: number;
  symmetricFrontChance: number;
  palette: PaletteConfig;
}

export const STYLE_PRESETS: Record<StylePreset, StylePresetSpec> = {
  modern: {
    id: 'modern',
    label: 'Modern',
    description: 'Flat roof, clean rectangular massing, generous openings.',
    lotWidth: { min: 14, max: 22 },
    lotDepth: { min: 14, max: 22 },
    footprintCoverage: { min: 0.45, max: 0.6 },
    aspectRatio: { min: 1.0, max: 1.6 },
    storyHeight: { min: 3.0, max: 3.3 },
    storyCountWeights: { 1: 1, 2: 3, 3: 1 },
    roofWeights: { flat: 6, gable: 1, hip: 0.5 },
    roofOverhang: { min: 0.3, max: 0.8 },
    windowDensity: 0.35,
    minCornerOffset: 0.8,
    fenceChance: 0.3,
    mailboxChance: 0.6,
    symmetricFrontChance: 0.3,
    palette: DEFAULT_PALETTES.moodyTall!,
  },
  suburban: {
    id: 'suburban',
    label: 'Suburban',
    description: 'Gable or hip roof, balanced openings, centered entrance.',
    lotWidth: { min: 12, max: 20 },
    lotDepth: { min: 14, max: 24 },
    footprintCoverage: { min: 0.35, max: 0.5 },
    aspectRatio: { min: 1.1, max: 1.5 },
    storyHeight: { min: 2.8, max: 3.1 },
    storyCountWeights: { 1: 2, 2: 5, 3: 0.5 },
    roofWeights: { flat: 0.5, gable: 5, hip: 3 },
    roofOverhang: { min: 0.4, max: 1.0 },
    windowDensity: 0.28,
    minCornerOffset: 1.0,
    fenceChance: 0.6,
    mailboxChance: 0.85,
    symmetricFrontChance: 0.7,
    palette: DEFAULT_PALETTES.warmModern!,
  },
  compact: {
    id: 'compact',
    label: 'Compact',
    description: 'Small lot, tight footprint, modest openings.',
    lotWidth: { min: 8, max: 12 },
    lotDepth: { min: 10, max: 14 },
    footprintCoverage: { min: 0.5, max: 0.7 },
    aspectRatio: { min: 0.9, max: 1.3 },
    storyHeight: { min: 2.7, max: 3.0 },
    storyCountWeights: { 1: 3, 2: 4, 3: 0 },
    roofWeights: { flat: 2, gable: 3, hip: 1 },
    roofOverhang: { min: 0.3, max: 0.6 },
    windowDensity: 0.22,
    minCornerOffset: 0.7,
    fenceChance: 0.2,
    mailboxChance: 0.5,
    symmetricFrontChance: 0.4,
    palette: DEFAULT_PALETTES.terracottaCompact!,
  },
  tall: {
    id: 'tall',
    label: 'Tall',
    description: 'Slim footprint, 2–3 stories, vertical emphasis.',
    lotWidth: { min: 8, max: 12 },
    lotDepth: { min: 14, max: 20 },
    footprintCoverage: { min: 0.45, max: 0.6 },
    aspectRatio: { min: 0.5, max: 0.85 },
    storyHeight: { min: 2.9, max: 3.2 },
    storyCountWeights: { 1: 0, 2: 3, 3: 5 },
    roofWeights: { flat: 4, gable: 2, hip: 1 },
    roofOverhang: { min: 0.25, max: 0.55 },
    windowDensity: 0.3,
    minCornerOffset: 0.8,
    fenceChance: 0.35,
    mailboxChance: 0.55,
    symmetricFrontChance: 0.35,
    palette: DEFAULT_PALETTES.moodyTall!,
  },
  courtyard: {
    id: 'courtyard',
    label: 'Courtyard',
    description: 'Offset footprint, fence likely, implied internal separation.',
    lotWidth: { min: 14, max: 22 },
    lotDepth: { min: 14, max: 22 },
    footprintCoverage: { min: 0.35, max: 0.5 },
    aspectRatio: { min: 1.1, max: 1.6 },
    storyHeight: { min: 2.9, max: 3.2 },
    storyCountWeights: { 1: 2, 2: 4, 3: 1 },
    roofWeights: { flat: 2, gable: 3, hip: 3 },
    roofOverhang: { min: 0.4, max: 0.9 },
    windowDensity: 0.25,
    minCornerOffset: 0.9,
    fenceChance: 0.85,
    mailboxChance: 0.7,
    symmetricFrontChance: 0.3,
    palette: DEFAULT_PALETTES.pastelCourtyard!,
  },
};

export function presetList(): StylePresetSpec[] {
  return Object.values(STYLE_PRESETS);
}
